<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingRegion } from '../types'
import { useBankingNumber } from '../useBankingNumber'
import { CAPITAL_REGION, useBankingRegions } from '../useBankingRegions'
import WidgetBankingStatsBlock from '../WidgetBankingStatsBlock.vue'

type RegionsMeasure = 'deposits' | 'credits' | 'ratio'

const MEASURES: RegionsMeasure[] = ['deposits', 'credits', 'ratio']

/**
 * geoBoundaries ADM1 shape names to the API region keys. The Osh Region shape already covers
 * the city of Osh, as the NBKR "Osh" figures do. Bishkek has no shape of its own in ADM1
 * (it lies inside Chuy Region), so it is drawn as a point at BISHKEK_COORDS.
 */
const SHAPE_TO_REGION: Record<string, string> = {
  'Batken Region': 'batken',
  'Jalal-Abad Region': 'jalal-abad',
  'Issyk-Kul Region': 'issyk-kul',
  'Naryn Region': 'naryn',
  'Osh Region': 'osh',
  'Talas Region': 'talas',
  'Chuy Region': 'chui'
}

const POINT_REGION = CAPITAL_REGION
const BISHKEK_COORDS: [number, number] = [74.59, 42.87]

const MAP_COLORS = ['#dbeafe', '#93c5fd', '#2563eb', '#1e3a8a']

// Same name as registered in ./regions-map, which is imported lazily and so not imported here.
const KG_MAP_NAME = 'kg'
/** The map fits the box keeping its aspect ratio, centred, on any screen width. */
const MAP_BOX = {
  left: 4,
  right: 4,
  top: 32,
  bottom: 4,
  preserveAspect: 'contain',
  preserveAspectAlign: 'center',
  preserveAspectVerticalAlign: 'middle'
} as const
/** A white outline keeps labels readable on both the pale and the dark regions. */
const LABEL_HALO = { textBorderColor: '#ffffff', textBorderWidth: 2 }
/** geoBoundaries keeps the region name in `shapeName`, ECharts reads `name` by default. */
const GEO_NAME_PROPERTY = 'shapeName'

const { t } = useI18n()
const { formatSom, formatNumber } = useBankingNumber()

const { regions, asOf, story } = useBankingRegions()

const measure = ref<RegionsMeasure>('deposits')

const measureValue = (region: IBankingRegion, by: RegionsMeasure = measure.value): number =>
  by === 'ratio' ? region.credits_per_deposit : region[by]

const formatMeasure = (value: number, by: RegionsMeasure = measure.value): string =>
  by === 'ratio' ? formatNumber(value, 2) : formatSom(value)

const regionName = (key: string, form: 'names' | 'names_full' = 'names'): string =>
  t(`banking_statistics.regions.${form}.${key}`)

const title = computed<{ key: string; params: Record<string, string | number> }>(() => {
  if (!story.value) {
    return { key: 'banking_statistics.regions.title_neutral', params: {} }
  }

  const { capital, leader } = story.value

  return {
    key: 'banking_statistics.regions.title',
    params: {
      share: `${formatNumber(capital.deposits_share, 1)}%`,
      region_in: t(`banking_statistics.regions.names_in.${leader.key}`),
      ratio: formatNumber(leader.credits_per_deposit, 2)
    }
  }
})

const ranking = computed(() => {
  const sorted = [...regions.value].sort((a, b) => measureValue(b) - measureValue(a))
  const max = Math.max(...sorted.map((region) => measureValue(region)), 0)

  return sorted.map((region) => ({
    key: region.key,
    name: regionName(region.key),
    value: formatMeasure(measureValue(region)),
    width: max > 0 ? Math.max((measureValue(region) / max) * 100, 1) : 0
  }))
})

// The map is drawn in the browser only: the GeoJSON is fetched and registered on mount.
const isMapReady = ref(false)
const hasMapFailed = ref(false)

const reportUnmappedShapes = (shapeNames: string[]) => {
  const unknown = shapeNames.filter((name) => !(name in SHAPE_TO_REGION))

  if (unknown.length) {
    console.error('[banking-statistics] map shapes without a region key:', unknown)
  }
}

onMounted(async () => {
  try {
    const { registerKgMap } = await import('./regions-map')
    const geoJson = await registerKgMap()

    if (import.meta.dev) {
      reportUnmappedShapes(geoJson.features.map((feature) => feature.properties.shapeName))
    }

    isMapReady.value = true
  } catch (error) {
    hasMapFailed.value = true
    console.error('[banking-statistics] the regions map failed to load', error)
  }
})

// Every API region must be either a shape or the Bishkek point, otherwise it would vanish silently.
watch(
  regions,
  (list) => {
    if (!import.meta.dev || !import.meta.client) return

    const drawn = new Set([...Object.values(SHAPE_TO_REGION), POINT_REGION])
    const missing = list.filter((region) => !drawn.has(region.key)).map((region) => region.key)

    if (missing.length) {
      console.error('[banking-statistics] regions missing on the map:', missing)
    }
  },
  { immediate: true }
)

const tooltipHtml = (key: string): string => {
  const region = regions.value.find((item) => item.key === key)

  if (!region) return ''

  const rows = MEASURES.map((by) => {
    const line = `${t(`banking_statistics.regions.measure_${by}`)}: <strong>${formatMeasure(measureValue(region, by), by)}</strong>`

    return by === measure.value ? `<span style="color:#1d4ed8">${line}</span>` : line
  })

  rows.push(
    t('banking_statistics.regions.deposits_share', {
      share: `${formatNumber(region.deposits_share, 1)}%`
    })
  )

  const note =
    region.key === 'osh'
      ? `<br/><span style="color:#6b7280;font-size:11px">${t('banking_statistics.regions.osh_note')}</span>`
      : ''

  return `<strong>${regionName(region.key, 'names_full')}</strong><br/>${rows.join('<br/>')}${note}`
}

const chartOption = computed<EChartsOption>(() => {
  const shaped = regions.value.filter((region) => region.key !== POINT_REGION)
  const point = regions.value.find((region) => region.key === POINT_REGION)
  const values = shaped.map((region) => measureValue(region))
  const min = values.length ? Math.min(...values) : 0
  const max = values.length ? Math.max(...values) : 1

  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: unknown) => tooltipHtml((params as { name?: string }).name ?? '')
    },
    // The scale covers the regions drawn as shapes: with Bishkek in it every region would be
    // the same pale colour. Bishkek is the labelled point with its value written next to it.
    visualMap: {
      show: false,
      seriesIndex: 0,
      min,
      max: max > min ? max : min + 1,
      inRange: { color: MAP_COLORS }
    },
    // The map series and the geo component share one box, so the Bishkek point drawn in geo
    // coordinates lands on its place on the map. The geo layer itself is invisible.
    geo: {
      map: KG_MAP_NAME,
      ...MAP_BOX,
      nameProperty: GEO_NAME_PROPERTY,
      roam: false,
      silent: true,
      itemStyle: { areaColor: 'transparent', borderColor: 'transparent' },
      label: { show: false }
    },
    series: [
      {
        type: 'map',
        map: KG_MAP_NAME,
        ...MAP_BOX,
        nameProperty: GEO_NAME_PROPERTY,
        nameMap: SHAPE_TO_REGION,
        roam: false,
        selectedMode: false,
        itemStyle: { areaColor: '#e5e7eb', borderColor: '#ffffff', borderWidth: 1 },
        label: {
          show: true,
          fontSize: 10,
          color: '#111827',
          ...LABEL_HALO,
          formatter: (params: { name?: string }) => regionName(params.name ?? '')
        },
        emphasis: {
          itemStyle: { areaColor: '#fbbf24' },
          label: { show: true, color: '#111827' }
        },
        data: shaped.map((region) => ({ name: region.key, value: measureValue(region) }))
      },
      {
        type: 'scatter',
        coordinateSystem: 'geo',
        symbolSize: 12,
        itemStyle: { color: '#ffffff', borderColor: '#1e3a8a', borderWidth: 3 },
        label: {
          show: true,
          position: 'top',
          fontSize: 11,
          fontWeight: 600,
          color: '#1e3a8a',
          // A white pill: the point sits on Chuy, which is often the darkest region.
          backgroundColor: '#ffffff',
          borderColor: '#bfdbfe',
          borderWidth: 1,
          borderRadius: 4,
          padding: [3, 6],
          distance: 6,
          formatter: () =>
            point ? `${regionName(point.key)}: ${formatMeasure(measureValue(point))}` : ''
        },
        data: point ? [{ name: point.key, value: [...BISHKEK_COORDS, measureValue(point)] }] : []
      }
    ]
  }
})
</script>

<template>
  <WidgetBankingStatsBlock id="regions" :title-key="title.key" :title-params="title.params" :as-of="asOf">
    <div data-testid="regions-measure" class="mb-4 flex flex-wrap gap-2">
      <Button
        v-for="item in MEASURES"
        :key="item"
        :variant="measure === item ? 'blue' : 'outline-gray'"
        size="sm"
        :aria-pressed="measure === item"
        @click="measure = item"
      >
        {{ t(`banking_statistics.regions.measure_${item}`) }}
      </Button>
    </div>

    <div data-testid="regions-map" class="mb-5">
      <LazyVChart
        v-if="isMapReady"
        :option="chartOption"
        autoresize
        class="h-52 w-full sm:h-96"
      />
      <div v-else-if="!hasMapFailed" class="h-52 w-full animate-pulse rounded-lg bg-gray-100 sm:h-96" />
    </div>

    <ol class="space-y-1.5" :aria-label="t(`banking_statistics.regions.measure_${measure}`)">
      <li
        v-for="row in ranking"
        :key="row.key"
        data-testid="regions-ranking-row"
        class="flex items-center gap-2 text-sm"
      >
        <span class="w-24 shrink-0 truncate text-gray-700">{{ row.name }}</span>
        <span class="h-3 min-w-0 flex-1 rounded-sm bg-gray-100">
          <span
            class="block h-full rounded-sm bg-blue-600 transition-[width] duration-300"
            :style="{ width: `${row.width}%` }"
          />
        </span>
        <span class="w-24 shrink-0 text-right font-semibold tabular-nums text-gray-900">{{ row.value }}</span>
      </li>
    </ol>

    <template #footer>
      <span>{{ t('banking_statistics.regions.attribution') }}</span>
    </template>
  </WidgetBankingStatsBlock>
</template>
